/* ─────────────────────────  src/components/login/LoginSignupModal.jsx  ───────────────────────── */
"use client";

import { useState, useCallback, useMemo } from "react";
import "./login.css";
import { useAuth } from "../../contexts"; // ← barrel import (`src/contexts/index.js`)

/* Preview-only Firestore stubs (safe to keep) */
const setDoc = async () => {};
const doc = () => {};
const serverTimestamp = () => Date.now();
// ----------------------------------------------------------------------------------------------

const LoginSignupModal = ({
  /* visibility controls from Header */
  isLoginOpen,
  onCloseLogin,
  onOpenSignup,

  isSignupOpen,
  onCloseSignup,
}) => {
  /* ───────── from AuthContext ───────── */
  const { user, login, signup, logout } = useAuth();

  /* ───────── UI & message state ───────── */
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' | 'error'

  /* ───────── Form state ───────── */
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    name: "",
    dob: "",
    email: "",
    mobile: "",
    password: "",
  });

  /* ───────── Field helpers ───────── */
  const changeLogin  = (field, v) => setLoginData((p) => ({ ...p, [field]: v }));
  const changeSignup = (field, v) => setSignupData((p) => ({ ...p, [field]: v }));

  /* ───────── Modal helpers ───────── */
  const resetForms = () => {
    setLoginData({ email: "", password: "" });
    setSignupData({ name: "", dob: "", email: "", mobile: "", password: "" });
    setMessage("");
    setMessageType("");
  };

  const closeAndReset = (fn) => {
    resetForms();
    setIsLoading(false);
    fn();
  };

  const overlayClick = (fn) => (e) => {
    if (e.target === e.currentTarget) closeAndReset(fn);
  };
  const stopProp = (e) => e.stopPropagation();

  /* ───────── Login submit ───────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      const { email, password } = loginData;
      if (!email || !password) throw new Error("Please fill in all required fields");

      await login(email, password);

      setMessage("Login successful!");
      setMessageType("success");
      setTimeout(() => closeAndReset(onCloseLogin), 400);
    } catch (err) {
      if (
        err.message?.includes("Account not found") ||
        err.code === "auth/user-not-found"
      ) {
        setMessage("Account doesn’t exist. Please sign-up first.");
      } else {
        setMessage(err.message || "Login failed. Please try again.");
      }
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ───────── Signup submit ───────── */
  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      const { name, dob, email, mobile, password } = signupData;
      if (!name || !email || !password) throw new Error("Please fill in all required fields");
      if (password.length < 6) throw new Error("Password must be at least 6 characters long");

      /* create auth + profile, auto-login */
      await signup(email, password, { name, dob, mobile });

      /* preview Firestore write */
      await setDoc(doc(null, "users", `uid-${Date.now()}`), {
        name,
        dob,
        email,
        mobile,
        createdAt: serverTimestamp(),
      });

      setMessage("Account created & signed in!");
      setMessageType("success");
      setTimeout(() => closeAndReset(onCloseSignup), 500);
    } catch (err) {
      setMessage(err.message || "Signup failed. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };


  /** 2️⃣  Login modal (unchanged form) */
  const LoginModal = useMemo(
    () => (
      <div className="modal-overlay" onClick={overlayClick(onCloseLogin)}>
        <div className="modal-container pop-border-radius" onClick={stopProp}>
          <div className="modal-header">
            <button
              className="close-button"
              onClick={() => closeAndReset(onCloseLogin)}
            >
              ×
            </button>
            <div className="modal-logo">
              <img src="/logo.svg" alt="Logo" className="logo-image" />
            </div>
            <h2 className="modal-title">Welcome Back</h2>
            <p className="modal-subtitle">Sign in to your account</p>
          </div>

          <div className="modal-body">
            <form
              className={`auth-form ${isLoading ? "loading" : ""}`}
              onSubmit={handleLogin}
            >
              <div className="form-group">
                <label htmlFor="login-email" className="form-label">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="form-input pop-border-radius"
                  placeholder="Enter your email"
                  value={loginData.email}
                  onChange={(e) => changeLogin("email", e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="login-password" className="form-label">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  className="form-input pop-border-radius"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(e) => changeLogin("password", e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              {message && (
                <div
                  className={
                    messageType === "success"
                      ? "success-message pop-border-radius"
                      : "error-message pop-border-radius"
                  }
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="auth-button primary pop-border-radius"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <div className="form-footer">
              <div className="form-divider">
                <span>Don't have an account?</span>
              </div>
              <button
                className="auth-button secondary pop-border-radius"
                onClick={() => {
                  closeAndReset(onCloseLogin);
                  onOpenSignup();
                }}
                disabled={isLoading}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    [
      loginData,
      isLoading,
      message,
      messageType,
      onCloseLogin,
      onOpenSignup,
    ],
  );

  /** 3️⃣  Signup modal (unchanged form) */
  const SignupModal = useMemo(
    () => (
      <div className="modal-overlay" onClick={overlayClick(onCloseSignup)}>
        <div className="modal-container pop-border-radius" onClick={stopProp}>
          <div className="modal-header">
            <button
              className="close-button"
              onClick={() => closeAndReset(onCloseSignup)}
            >
              ×
            </button>
            <div className="modal-logo">
              <img src="/logo.svg" alt="Logo" className="logo-image" />
            </div>
            <h2 className="modal-title">Welcome to Welsh Education</h2>
            <p className="modal-subtitle">Join us today</p>
          </div>

          <div className="modal-body">
            <form
              className={`auth-form ${isLoading ? "loading" : ""}`}
              onSubmit={handleSignup}
            >
              <div className="form-group">
                <label htmlFor="signup-name" className="form-label">
                  Full Name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  className="form-input pop-border-radius"
                  placeholder="Enter your full name"
                  value={signupData.name}
                  onChange={(e) => changeSignup("name", e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="signup-dob" className="form-label optional">
                  Date of Birth
                </label>
                <input
                  id="signup-dob"
                  type="date"
                  className="form-input pop-border-radius"
                  value={signupData.dob}
                  onChange={(e) => changeSignup("dob", e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="signup-email" className="form-label">
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  className="form-input pop-border-radius"
                  placeholder="Enter your email"
                  value={signupData.email}
                  onChange={(e) => changeSignup("email", e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="signup-mobile" className="form-label optional">
                  Mobile Number
                </label>
                <input
                  id="signup-mobile"
                  type="tel"
                  className="form-input pop-border-radius"
                  placeholder="Enter your mobile number"
                  value={signupData.mobile}
                  onChange={(e) => changeSignup("mobile", e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="signup-password" className="form-label">
                  Create Password
                </label>
                <input
                  id="signup-password"
                  type="password"
                  className="form-input pop-border-radius"
                  placeholder="Create a strong password"
                  value={signupData.password}
                  onChange={(e) => changeSignup("password", e.target.value)}
                  required
                  disabled={isLoading}
                  minLength={6}
                />
              </div>

              {message && (
                <div
                  className={
                    messageType === "success"
                      ? "success-message pop-border-radius"
                      : "error-message pop-border-radius"
                  }
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="auth-button primary pop-border-radius"
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    ),
    [
      signupData,
      isLoading,
      message,
      messageType,
      onCloseSignup,
    ],
  );

  /* ───────── Final render ───────── */
  return (
    <>
      {isLoginOpen && ( LoginModal)}
      {isSignupOpen && SignupModal}
    </>
  );
};

export default LoginSignupModal;

/* ────────────────────────────────  End of file  ─────────────────────────────── */
